import {
  Container,
  FormControl,
  FormControlProps,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import tokens from '@verifime/design-tokens';
import { TOption } from '@verifime/utils';
import { useEffect, useState } from 'react';
import { Control, Controller, FieldValues, UseFormSetError } from 'react-hook-form';
import { formFieldLabel } from '@componentsroot/Form/FormGenerator/utils';
import ComboBox from '@componentsroot/ComboBox';
import SearchItemDisplay from '@componentsroot/SearchItemDisplay';

type TValue = string | string[] | undefined;

type TReactHookFormSearchSelectProps = {
  name: string;
  label: string;
  control: Control<FieldValues, TValue>;
  setError?: UseFormSetError<FieldValues>;
  defaultValue?: TValue;
  required?: boolean;
  onOptionChange?: (event: SelectChangeEvent) => void;
  // see on why https://github.com/mui/material-ui/issues/18580
  // Select component does not support all margin property like other components,
  // it must be put into FormControl component to support margin property.
  margin?: FormControlProps['margin'];
  size?: SelectProps<TValue>['size'];
  optionProvider: (searchText: string) => Promise<TOption[]>;
} & Omit<SelectProps<TValue>, 'margin'>;

export default function FormComboBox({
  name,
  label,
  control,
  setError,
  required = false,
  defaultValue = '',
  margin = 'normal',
  sx,
  optionProvider,
  disabled,
  onOptionChange,
}: TReactHookFormSearchSelectProps) {
  const requiredLabel = formFieldLabel(label, required);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<TOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<TOption | null>(null);
  const [isSearching, setSearching] = useState(false);

  useEffect(() => {
    if (defaultValue) {
      optionProvider(defaultValue as string)
        .then((data) => {
          setOptions(data);
          setSearchText(data[0].label);
        })
        .catch((err) => {
          console.error(err);
          setError?.(name, { message: err?.message ?? String(err), type: 'server' });
        });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (searchText === '') {
      setOptions([]);
      return;
    }
    setSearching(true);
    optionProvider(searchText)
      .then((data) => setOptions(data))
      .catch((err) => {
        console.error(err);
        setError?.(name, { message: err?.message ?? String(err), type: 'server' });
      })
      .finally(() => setSearching(false));
  }, [searchText]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <>
            <FormControl
              sx={{
                flex: '1',
                marginTop: tokens.spacingXs,
                marginBottom: tokens.spacing2xs,
                '&.MuiFormControl-root .MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root.MuiOutlinedInput-root':
                  {
                    padding: `${tokens.spacing4xs} ${tokens.spacingXs}`,
                  },
                '&.MuiFormControl-root .MuiAutocomplete-root .MuiFormControl-root .MuiFormLabel-root.MuiInputLabel-root':
                  {
                    marginTop: `-${tokens.spacingXs}`,
                    color: !!fieldState.error?.message ? (theme) => theme.palette.error.main : null,
                  },
                '&.MuiFormControl-root .MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: !!fieldState.error?.message
                      ? (theme) => theme.palette.error.main
                      : null,
                  },
                ...sx,
              }}
              fullWidth
              error={!!fieldState.error?.message}
              margin={margin}
            >
              <ComboBox
                fullWidth
                autoSelect
                disableClearable
                clearOnBlur={false}
                ListboxProps={{ style: { maxHeight: tokens.sizeSm } }}
                label={requiredLabel as string}
                inputValue={searchText}
                value={selectedOption}
                options={options}
                freeSolo={searchText === '' ? true : false}
                loading={isSearching}
                loadingText="Searching"
                noOptionsText="No result found"
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => {
                  return option.code === value.code;
                }}
                disabled={disabled}
                renderOption={(props, { code, label }, state) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Container key={key} component="li" {...optionProps}>
                      <SearchItemDisplay key={code} name={label} value={code} />
                    </Container>
                  );
                }}
                onInputChange={(event, newSearchText) => {
                  if (!event) {
                    return;
                  }

                  setSearchText(newSearchText);
                }}
                onChange={(event: any, newSelectedOption) => {
                  if (!newSelectedOption) {
                    return;
                  }

                  const { code } = newSelectedOption as TOption;
                  // Not `event.target.value = code`. `event.target` is whatever the pointer
                  // landed on, and on an option that is often the <li> itself, whose `value` is
                  // a reflected integer attribute: assigning 'NZL' to it reads back as 0. Both
                  // the form value and onOptionChange then received 0 instead of the country.
                  const optionEvent = { ...event, target: { value: code } };
                  field.onChange(optionEvent);

                  setSelectedOption(newSelectedOption as TOption);
                  onOptionChange?.(optionEvent);
                }}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          </>
        );
      }}
    />
  );
}
